import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { AppShell } from '@/components/layout/AppShell'
import { ProtectedRoute } from './ProtectedRoute'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import { placeholderFor } from '@/features/placeholder/ModulePlaceholder'

// Login is the only route an unauthenticated visitor can reach, so it is the one
// screen worth loading eagerly — lazy-loading it just adds a spinner before the
// first paint of the first thing anyone sees.
import LoginPage from '@/features/auth/LoginPage'

// Reachable signed OUT — an invited person has no account yet — so it sits
// outside ProtectedRoute, alongside /login.
const AcceptInvitePage = lazy(() => import('@/features/auth/AcceptInvitePage'))

const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'))
const BankAccountsPage = lazy(() => import('@/modules/banks/BankAccountsPage'))
const BankStatementPage = lazy(() => import('@/modules/banks/BankStatementPage'))
const ContactsPage = lazy(() => import('@/modules/contacts/ContactsPage'))
const CategoriesPage = lazy(() => import('@/modules/categories/CategoriesPage'))
const ExpensesPage = lazy(() => import('@/modules/expenses/ExpensesPage'))
const TransfersPage = lazy(() => import('@/modules/transfers/TransfersPage'))
const IncomesPage = lazy(() => import('@/modules/incomes/IncomesPage'))
const InvoicesPage = lazy(() => import('@/modules/invoices/InvoicesPage'))
const PaymentsPage = lazy(() => import('@/modules/payments/PaymentsPage'))
const LoansPage = lazy(() => import('@/modules/loans/LoansPage'))
const ProductsPage = lazy(() => import('@/modules/products/ProductsPage'))
const InvestmentsPage = lazy(() => import('@/modules/investments/InvestmentsPage'))
const WealthTrackerPage = lazy(() => import('@/modules/wealth/WealthTrackerPage'))
const AuditLogPage = lazy(() => import('@/modules/audit/AuditLogPage'))
const SettingsPage = lazy(() => import('@/modules/settings/SettingsPage'))
// Lazy on purpose: the builder's live preview pulls ~820 KB of pdfmake on mount,
// which nobody opening Settings to add a teammate should have to download.
const InvoiceTemplatePage = lazy(() => import('@/modules/settings/InvoiceTemplatePage'))
const ReportsIndexPage = lazy(() => import('@/modules/reports/ReportsIndexPage'))
const ProfitLossPage = lazy(() => import('@/modules/reports/ProfitLossPage'))
const BalanceSheetPage = lazy(() => import('@/modules/reports/BalanceSheetPage'))
const TrialBalancePage = lazy(() => import('@/modules/reports/TrialBalancePage'))
const DayBookPage = lazy(() => import('@/modules/reports/DayBookPage'))
const GstSummaryPage = lazy(() => import('@/modules/reports/GstSummaryPage'))
const ContactWisePage = lazy(() => import('@/modules/reports/ContactWisePage'))
const CategoryWisePage = lazy(() => import('@/modules/reports/CategoryWisePage'))
const ProductRevenuePage = lazy(() => import('@/modules/reports/ProductRevenuePage'))
const AccountWisePage = lazy(() => import('@/modules/reports/AccountWisePage'))

function RouteFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Loader2 className="size-5 animate-spin text-fg-muted" aria-label="Loading" />
    </div>
  )
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>
}

/**
 * Every module is routed from the start, even where the screen is a placeholder,
 * so the sidebar, command palette and `g`-shortcuts are exercised for real
 * rather than wired up blind at the end.
 */
export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/accept-invite',
    element: (
      <Lazy>
        <AcceptInvitePage />
      </Lazy>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Lazy><DashboardPage /></Lazy> },

      { path: 'contacts', element: <Lazy><ContactsPage /></Lazy> },
      { path: 'categories', element: <Lazy><CategoriesPage /></Lazy> },
      { path: 'bank-accounts', element: <Lazy><BankAccountsPage /></Lazy> },
      { path: 'bank-accounts/:bankId/statement', element: <Lazy><BankStatementPage /></Lazy> },

      { path: 'expenses', element: <Lazy><ExpensesPage /></Lazy> },
      { path: 'incomes', element: <Lazy><IncomesPage /></Lazy> },
      { path: 'invoices', element: <Lazy><InvoicesPage /></Lazy> },
      { path: 'payments', element: <Lazy><PaymentsPage /></Lazy> },
      { path: 'bank-transfers', element: <Lazy><TransfersPage /></Lazy> },
      { path: 'loans', element: <Lazy><LoansPage /></Lazy> },
      { path: 'products', element: <Lazy><ProductsPage /></Lazy> },
      { path: 'investments', element: <Lazy><InvestmentsPage /></Lazy> },
      { path: 'wealth', element: <Lazy><WealthTrackerPage /></Lazy> },

      { path: 'reports', element: <Lazy><ReportsIndexPage /></Lazy> },
      { path: 'reports/profit-loss', element: <Lazy><ProfitLossPage /></Lazy> },
      { path: 'reports/balance-sheet', element: <Lazy><BalanceSheetPage /></Lazy> },
      { path: 'reports/trial-balance', element: <Lazy><TrialBalancePage /></Lazy> },
      { path: 'reports/day-book', element: <Lazy><DayBookPage /></Lazy> },
      { path: 'reports/gst', element: <Lazy><GstSummaryPage /></Lazy> },
      { path: 'reports/contact-wise', element: <Lazy><ContactWisePage /></Lazy> },
      { path: 'reports/category-wise', element: <Lazy><CategoryWisePage /></Lazy> },
      { path: 'reports/product-revenue', element: <Lazy><ProductRevenuePage /></Lazy> },
      { path: 'reports/account-wise', element: <Lazy><AccountWisePage /></Lazy> },

      { path: 'audit-log', element: <Lazy><AuditLogPage /></Lazy> },
      { path: 'settings', element: <Lazy><SettingsPage /></Lazy> },
      { path: 'settings/invoice-template', element: <Lazy><InvoiceTemplatePage /></Lazy> },

      { path: '*', element: placeholderFor('Page not found', 'That route does not exist.') },
    ],
  },
])
